import { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useAppDispatch } from 'Redux/store';
import { getLoadList } from 'Redux/Load/reducer';
import { useSelector } from 'react-redux';
import {
  selectLoadList,
  selectLoadCount,
  selectLoadLoading,
} from 'Redux/Load/slice';
import PageHeader from 'Components/PageHeader/PageHeader';
import { Segmented } from 'antd';
import { CustomTag } from 'Components/CustomTag/CustomTag';
import PageTable from 'Components/PageTable/PageTable';
import Paginations from 'Components/UI/Pagination/Pagination';
import { getUser } from 'Redux/User/reducer';
import { formatDate } from 'Shared/Utils';
import { IUser } from 'Shared/Types';
import usePrevious from 'Shared/Hooks/usePrevious';
import styles from './LoadsList.module.scss';

type LoadType = 'Active' | 'Check Out' | 'Completed' | 'History';

type UserInfo = {
  [key: number]: {
    firstName: string;
    lastName: string;
  };
};

export const LoadsList = () => {
  const dispatch = useAppDispatch();
  const loadList = useSelector(selectLoadList);
  const loadListIsLoading = useSelector(selectLoadLoading);
  const loadCount = useSelector(selectLoadCount);
  const [loadTypeValue, setLoadTypeValue] = useState<LoadType>('Active');
  const previousLoadTypeValue = usePrevious(loadTypeValue);
  const [usersInfo, setUsersInfo] = useState<UserInfo>({});
  const location = useLocation();
  const baseComponentURL = '/my-loads';

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageSize = Number(params.get('page_size'));
    let page = Number(params.get('page')) || 1;
    let category = '';

    if (loadTypeValue !== previousLoadTypeValue) {
      page = 1;
    }

    if (loadTypeValue === 'Check Out') {
      category = 'checkout';
    } else if (loadTypeValue === 'Completed') {
      category = 'complete';
    } else if (loadTypeValue === 'History') {
      category = 'history';
    }

    dispatch(getLoadList({ page, pageSize, category }));
  }, [location.search, loadTypeValue, dispatch]);

  useEffect(() => {
    const existingDispatcherIds = new Set<number>(
      Object.keys(usersInfo).map(Number),
    );
    const newDispatcherIds = new Set<number>();

    loadList.forEach((load) => {
      const dispatcherId = load?.letter?.dispatcher_id;

      if (dispatcherId && !existingDispatcherIds.has(dispatcherId)) {
        newDispatcherIds.add(dispatcherId);
      }
    });

    const newUsersInfo: UserInfo = {};

    newDispatcherIds.forEach((id) => {
      dispatch(getUser(id)).then((user) => {
        if (user.payload) {
          const { first_name, last_name } = user.payload as IUser;

          newUsersInfo[id] = {
            firstName: first_name,
            lastName: last_name,
          };

          setUsersInfo((prevUsersInfo) => ({
            ...prevUsersInfo,
            ...newUsersInfo,
          }));
        }
      });
    });
  }, [loadList]);

  return (
    <div className={styles.loadsList}>
      <PageHeader content="My Loads" />

      <Segmented
        className={styles.loadsList__switcher}
        options={['Active', 'Check Out', 'Completed', 'History']}
        onChange={(value) => setLoadTypeValue(value as LoadType)}
        defaultValue={loadTypeValue}
      />

      <PageTable
        titles={[
          'Created',
          'Load ID',
          'Pick-up and delivery',
          'Broker',
          'Dispatcher',
          'Closed time',
          'Quick pay',
          'Status',
        ]}
        rows={
          Boolean(loadList.length)
            ? loadList
                .filter((load) => load.letter)
                .map((load) => {
                  let userInfo = {
                    firstName: '',
                    lastName: '',
                  };

                  if (usersInfo[load.letter.dispatcher_id]) {
                    userInfo = usersInfo[load.letter.dispatcher_id];
                  }

                  return {
                    id: load.id,
                    expiresInMs: new Date(load.expires).getTime(),
                    items: [
                      formatDate({
                        date: load.created_at,
                        outputFormat: 'TIME_date',
                      }),
                      load.order_number,
                      <>
                        <div>{load.pick_up_at}</div>
                        <div>{load.deliver_to}</div>
                      </>,
                      load.broker,
                      [userInfo.firstName, userInfo.lastName].join(' '),
                      '',
                      '',
                      <CustomTag content={load.order_status} />,
                    ],
                  };
                })
            : []
        }
        baseComponentURL={'/my-load'}
        isLoading={loadListIsLoading}
      />

      {loadTypeValue === previousLoadTypeValue && loadList.length > 0 && (
        <Paginations
          baseComponentURL={baseComponentURL}
          totalCount={loadCount}
        />
      )}
    </div>
  );
};
